import {Inject, Injectable} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {combineLatest, of, switchMap} from 'rxjs';
import {catchError, filter, map, take, tap} from 'rxjs/operators';
import {GlobalService} from '../../../../../../global/global.service';
import {
    ICCBulkUploadFilesService, IUploadFilesServiceToken
} from '../../../../contract-control/modals/bulk-upload-modal/upload-files.service';
import * as ContractControlAction from '../../../../contract-control/store/actions/contract-control.action';
import * as BulkUploadActions from './documents-bulk-upload.actions';
import {DocumentBulkUploadStepEnum} from './documents-bulk-upload.reducers';
import * as BulkUploadSelector from './documents-bulk-upload.selectors';


@Injectable({providedIn: 'root'})
export class DocumentsBulkUploadEffects {

    constructor(private _action: Actions,
                private _store: Store,
                private _globalService: GlobalService,
                private _translocoService:TranslocoService,
                @Inject(IUploadFilesServiceToken) private _uploadService: ICCBulkUploadFilesService,) {
    }

    nextStep = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.nextStep),
            switchMap(() =>
                this._store.select(BulkUploadSelector.currentStep)
                    .pipe(
                        map((currentStep) => {
                            let nextStep = DocumentBulkUploadStepEnum.uploadCSV;

                            if (currentStep == DocumentBulkUploadStepEnum.uploadCSV) {
                                nextStep = DocumentBulkUploadStepEnum.mapColumn;
                            } else if (currentStep == DocumentBulkUploadStepEnum.mapColumn) {
                                nextStep = DocumentBulkUploadStepEnum.preview;
                            } else if (currentStep == DocumentBulkUploadStepEnum.preview) {
                                nextStep = DocumentBulkUploadStepEnum.uploadPDF;
                            } else if (currentStep == DocumentBulkUploadStepEnum.uploadPDF) {
                                nextStep = DocumentBulkUploadStepEnum.matchFiles;
                            }

                            return BulkUploadActions.changeStep({step: nextStep});
                        }),
                        take(1)
                    )
            )
        )
    });

    prevStep = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.prevStep),
            switchMap(() =>
                this._store.select(BulkUploadSelector.currentStep)
                    .pipe(
                        map((currentStep) => {
                            let prevStep = DocumentBulkUploadStepEnum.uploadCSV;

                            if (currentStep == DocumentBulkUploadStepEnum.mapColumn) {
                                prevStep = DocumentBulkUploadStepEnum.uploadCSV;
                            } else if (currentStep == DocumentBulkUploadStepEnum.preview) {
                                prevStep = DocumentBulkUploadStepEnum.mapColumn;
                            } else if (currentStep == DocumentBulkUploadStepEnum.uploadPDF) {
                                prevStep = DocumentBulkUploadStepEnum.uploadCSV;
                            } else if (currentStep == DocumentBulkUploadStepEnum.matchFiles) {
                                prevStep = DocumentBulkUploadStepEnum.uploadPDF;
                            } else if (currentStep == DocumentBulkUploadStepEnum.summary) {
                                prevStep = DocumentBulkUploadStepEnum.matchFiles
                            }

                            return BulkUploadActions.changeStep({step: prevStep});
                        }),
                        take(1)
                    )
            )
        )
    });

    stepChange = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.changeStep),
            map(action => {
                return BulkUploadActions.changeStepResult({step: action.step})
            })
        )
    });

    skipCSVImportClicked = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.skipCSVImportClicked),
            map(() => BulkUploadActions.createImport()))
    });

    createImport = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.createImport),
            switchMap(() => this._uploadService.createImport()
                .pipe(
                    map(importData => BulkUploadActions.createImportSucceed({importData})),
                    catchError(error => of(BulkUploadActions.createImportFailed({error: error})))
                )
            )
        );
    });

    createImportSucceed = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.createImportSucceed),
            switchMap(() => this._uploadService.createImport()
            .pipe(
                map(preview => BulkUploadActions.fetchPreviewPDFImportSucceed({preview})),
                catchError(error => of(BulkUploadActions.fetchPreviewPDFImportFailed({error: error})))
                )
            )
        );
    });

    requestFailedFailed = createEffect(() => {
        return this._action.pipe(
            ofType(
                BulkUploadActions.createImportFailed,
                BulkUploadActions.fetchPreviewPDFImportFailed,
                BulkUploadActions.fetchFirstRowFailed,
                BulkUploadActions.fetchPotentialMappingsFailed,
                BulkUploadActions.saveMappingsFailed,
                BulkUploadActions.importXLSFailed,
                BulkUploadActions.importPDFFailed
            ),
            tap(({error}) => this._globalService.toastrError(error))
        );
    }, {
        dispatch: false
    });

    fetchFirstRow = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.fetchFirstRow),
            switchMap(() => combineLatest({
                    uploadId: this._store.select(BulkUploadSelector.uploadId).pipe(take(1)),
                    firstRowTitles: this._store.select(BulkUploadSelector.firstLineTitles).pipe(take(1))
                })
                    .pipe(
                        switchMap(x => this._uploadService.getFirstLine(x.uploadId, x.firstRowTitles)
                            .pipe(
                                map(result => BulkUploadActions.fetchFirstRowSucceed({firstLine: result})),
                                catchError(error => of(BulkUploadActions.fetchFirstRowFailed({error: error})))
                            )),
                    )
            )
        );
    });

    fetchPotentialMappings = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.fetchPotentialMappings),
            switchMap(()=>{
                return this._store.select(BulkUploadSelector.uploadId).pipe(take(1))
            }),
            switchMap((importId) => this._uploadService.getPotentialMapping(importId)
                .pipe(
                    map(mapping => BulkUploadActions.fetchPotentialMappingsSucceed({mapping})),
                    catchError(error => of(BulkUploadActions.fetchPotentialMappingsFailed({error: error})))
                )),
        );
    });

    saveMappings = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.saveMappings),
            switchMap(() => combineLatest({
                mappings: this._store.select(BulkUploadSelector.mapping).pipe(take(1)),
                uploadId: this._store.select(BulkUploadSelector.uploadId).pipe(take(1)),
                cardConfig: this._store.select(BulkUploadSelector.cardColumns).pipe(take(1))
            })),
            switchMap(({mappings, cardConfig, uploadId}) => {
                const hasAnyMappingsSet = mappings.length === 0; 
                
                if (hasAnyMappingsSet) {
                    this._globalService.toastrWarning(this._translocoService.translate("At least one mapping has to be set validation toast (bulk upload) - You have to set up at least one column mapping"));
                    this._store.dispatch(BulkUploadActions.resetIsUpdateMappingRequestInProgress());
                }
                
                const allReruiredFieldsSet = (cardConfig ?? [])
                    .filter(x => x?.is_required === true)
                    .every(x => mappings.find(y => y.column === x.column) != null);
                
                if(allReruiredFieldsSet == false){
                    this._globalService.toastrWarning(this._translocoService.translate('Required field symbol caption (match column steps) - Fields marked with are mandatory', {mark: '*'}));
                }
                
                return of({
                    hasAnyMappingsSet:hasAnyMappingsSet,
                    allReruiredFieldsSet:allReruiredFieldsSet,
                    mappings:mappings,
                    uploadId:uploadId
                })
            }),
            filter(x => x.hasAnyMappingsSet === true && x.allReruiredFieldsSet === true),
            switchMap(x => this._uploadService.saveMapping(x.uploadId, x.mappings.map(x => x.is_empty === 0 ? x.column : null))
                .pipe(
                    map(mapping => BulkUploadActions.saveMappingsSucceed({mapping})),
                    catchError(error => of(BulkUploadActions.saveMappingsFailed({error: error})))
                )),
        );
    });

    saveMappingsSucceed = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.saveMappingsSucceed),

            tap(() => {
                this._store.dispatch(BulkUploadActions.nextStep());
            }));

    }, {
        dispatch: false
    });
 

    importXLS = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.importXLS),
            switchMap(() => combineLatest({
                uploadId: this._store.select(BulkUploadSelector.uploadId).pipe(take(1)),
                firstLineTitles: this._store.select(BulkUploadSelector.firstLineTitles).pipe(take(1)),
                mergeOrganisations: this._store.select(BulkUploadSelector.mergeOrganisations).pipe(take(1)),
                importTodoSettings: this._store.select(BulkUploadSelector.importSettings).pipe(take(1))
            })), switchMap(x => {
                return this._uploadService.setImportSettings(x.uploadId, {
                    first_line_titles: x.firstLineTitles === 1, merge_organizations: x.mergeOrganisations, todo: x.importTodoSettings
                })
            }),

            switchMap(x => this._uploadService.importXls(x.id, x.settings.first_line_titles === true? 1 as const: 0 as const, x.settings.merge_organizations)
                .pipe(
                    map(result => BulkUploadActions.importXLSSucceed({result})),
                    catchError(error => of(BulkUploadActions.importXLSFailed({error: error})))
                )),
        );
    });

    importXLSSucceed = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.importXLS),
            tap(() => {
                this._globalService.toastrSuccess('Import data structure has been imported');
                this._store.dispatch(BulkUploadActions.changeStep({step: DocumentBulkUploadStepEnum.uploadPDF}))
            }))
    }, {
        dispatch: false
    });

    importPDF = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.importPDF),
            switchMap(() => combineLatest({
                uploadId: this._store.select(BulkUploadSelector.uploadId).pipe(take(1)),
                failedImports: this._store.select(BulkUploadSelector.failedImports).pipe(take(1))
            })),
            switchMap(x => this._uploadService.importPDF(x.uploadId)
                .pipe(
                    map(importData => BulkUploadActions.importPDFSucceed({importData})),
                    catchError(error => of(BulkUploadActions.importPDFFailed({error: error})))
                )),
        );
    });

    importPDFSucceed = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.importPDFSucceed),
            switchMap(() => this._store.select(BulkUploadSelector.unimportedSummary).pipe(take(1))),
            tap((unimportedSummary) => {
                if (unimportedSummary?.length > 0) {
                    this._store.dispatch(BulkUploadActions.changeStep({step: DocumentBulkUploadStepEnum.summary}));
                } else {
                    this._store.dispatch(BulkUploadActions.closeModal());
                    this._globalService.toastrSuccess(this._translocoService.translate('Import PDF succeeded toast (bulk upload) - All files imported'));
                }

                this._store.dispatch(ContractControlAction.importPDFSuccessNotification())
            })
        );
    }, {
        dispatch: false
    });

importPDFSuccessNotification= createEffect(() => {
    return this._action.pipe(
        ofType(ContractControlAction.importPDFSuccessNotification),
    )}, {dispatch: false});

    saveImportSettings = createEffect(() => {
        return this._action
            .pipe(
                ofType(BulkUploadActions.saveImportSettings),
                switchMap(() => combineLatest({
                    uploadId: this._store.select(BulkUploadSelector.uploadId).pipe(take(1)),
                    settings: this._store.select(BulkUploadSelector.importSettings).pipe(take(1)),
                    firstLineTitles: this._store.select(BulkUploadSelector.firstLineTitles).pipe(take(1)),
                    mergeOrganisations: this._store.select(BulkUploadSelector.mergeOrganisations).pipe(take(1)),
                })),
                take(1),
                filter(x => x.settings != null && this.isFormFilled(x.settings.user_id, x.settings.due_date, x.settings.description) && x.uploadId != null),

                switchMap((x) => this._uploadService.setImportSettings(x.uploadId, {
                        merge_organizations: x.mergeOrganisations,
                        first_line_titles: x.firstLineTitles === 1,
                        todo: {
                            user_id: x.settings.user_id,
                            due_date: x.settings.due_date,
                            description: x.settings.description
                        }
                    })
                        .pipe(
                            map(() => BulkUploadActions.saveImportSettingsSucceed()),
                            catchError(error => of(BulkUploadActions.saveImportSettingsFailed({error})))
                        )
                ))
    });

    saveImportSettingsFailed = createEffect(() => {
        return this._action
            .pipe(
                ofType(BulkUploadActions.saveImportSettingsFailed),
                tap(error => this._globalService.toastrError(error))
            )
    },{
        dispatch:false
    });

    closeModal = createEffect(() => {
        return this._action.pipe(
            ofType(BulkUploadActions.closeModal)
        )
    }, {
        dispatch: false
    });

    private isFormFilled(userId, due_date, description): boolean{
        return  userId?.value != null || due_date?.value != null || due_date?.value?.trim().length > 0 || description?.value?.trim().length > 0
    }
}
